import React from 'react';
import { Loader2, Inbox } from 'lucide-react';

export const StatusBadge = ({ status, label }) => {
  const key = (status || '').toLowerCase();
  const text = label || (status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium"
      style={{ backgroundColor: `hsl(var(--status-${key}) / 0.12)`, color: `hsl(var(--status-${key}))`, borderColor: `hsl(var(--status-${key}) / 0.25)` }}
      data-testid={`status-badge-${key}`}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: `hsl(var(--status-${key}))` }} />
      {text}
    </span>
  );
};

const PRIORITY_STYLES = {
  urgent: 'bg-destructive/10 text-destructive border-destructive/25',
  high: 'bg-amber-500/10 text-amber-600 border-amber-500/25',
  normal: 'bg-primary/10 text-primary border-primary/25',
  low: 'bg-muted text-muted-foreground border-border',
};

export const PriorityBadge = ({ priority }) => {
  const p = (priority || 'normal').toLowerCase();
  const cls = PRIORITY_STYLES[p] || PRIORITY_STYLES.normal;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${cls}`}
      data-testid={`priority-badge-${p}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {p}
    </span>
  );
};


export const PaymentBadge = ({ status }) => {
  const s = status || 'Pending';
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium"
      style={{ backgroundColor: `hsl(var(--pay-${s}) / 0.12)`, color: `hsl(var(--pay-${s}))`, borderColor: `hsl(var(--pay-${s}) / 0.25)` }}
      data-testid={`payment-badge-${s.toLowerCase()}`}
    >
      {s}
    </span>
  );
};

export const KpiCard = ({ label, value, icon: Icon, sub, testId }) => (
  <div className="rounded-xl border border-border bg-card p-4 sm:p-5 shadow-sm animate-fade-up" data-testid={testId}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl sm:text-3xl font-semibold tabular kpi-num font-display">{value}</p>
        {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
      </div>
      {Icon && (
        <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-muted">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      )}
    </div>
  </div>
);

export const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="text-lg sm:text-xl font-semibold tracking-tight font-display">{children}</h2>
    {action}
  </div>
);

export const Loading = ({ label = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground" data-testid="loading-state">
    <span className="bg-white rounded-xl px-3 py-2 shadow-md border border-border/60">
      <img src="/assets/logo.png" alt="Mobile Fuel Kart" className="h-7" />
    </span>
    <span className="flex items-center"><Loader2 className="h-5 w-5 animate-spin mr-2" /> {label}</span>
  </div>
);

export const EmptyState = ({ title, desc, action, icon: Icon = Inbox }) => (
  <div className="flex flex-col items-center justify-center text-center py-14 px-4 rounded-xl border border-dashed border-border bg-card" data-testid="empty-state">
    <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
      <Icon className="h-6 w-6 text-muted-foreground" />
    </div>
    <p className="font-semibold">{title}</p>
    {desc && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{desc}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);
